/**
 * Los umbrales del semáforo de ratios financieros, en UN SOLO SITIO.
 *
 * Vivían en `apps/api/src/lib/accounting/kpis.ts` y estaban COPIADOS a mano en
 * `apps/web/src/lib/ratio-insight.ts`, que es quien redacta la frase que
 * explica el ratio. El backend decidía el color y la pantalla el texto con los
 * mismos números escritos dos veces: tocar uno y olvidar el otro dejaba la
 * tarjeta diciendo «holgado» en rojo. Hoy coincidían; era cuestión de tiempo.
 *
 * Están aquí, en `@jr2os/shared`, porque es el único paquete que ven los dos.
 */
export const SEMAFORO_RULES = {
    liquidezCorriente: {
        minGreen: 1.5,
        maxGreen: 2.2,
        // Amarillo en [1.0, 1.5) ∪ (2.2, +∞). Modelamos la segunda banda con
        // extraYellow; la inferior con [1.0, 1.5).
        minYellow: 1.0,
        maxYellow: 1.4999999,
        extraYellow: { min: 2.2000001, max: Infinity },
    },
    // Prueba ácida: green [0.9, 1.2], yellow [0.6, 0.9) ∪ (1.2, +∞), red < 0.6. Por encima de 1,2
    // es «exceso de activos líquidos»: precaución, no riesgo (22-10-2026; antes daba rojo).
    pruebaAcida: {
        minGreen: 0.9,
        maxGreen: 1.2,
        minYellow: 0.6,
        maxYellow: 0.8999999,
        extraYellow: { min: 1.2000001, max: Infinity },
    },
    // Fondo de maniobra: green > 0, red <= 0 (sin amarillo). Lo modelamos como
    // green ∈ (0, +∞], red en cualquier otro caso.
    fondoManiobra: {
        minGreen: 0.0000001,
        maxGreen: Infinity,
    },
    // Endeudamiento (PT/PN): green [0.4, 0.6], yellow [0, 0.4) ∪ (0.6, 0.8],
    // red < 0 (patrimonio neto NEGATIVO) o > 0.8.
    // 22-10-2026: poca deuda (< 0,2) pasó de rojo a amarillo: «infra-apalancamiento» es capacidad
    // de financiación sin usar, no peligro (el texto de la tarjeta siempre lo dijo, y Ledgeria lo
    // pinta ámbar). El rojo por abajo queda para un PN negativo, que sí lo es.
    endeudamiento: {
        minGreen: 0.4,
        maxGreen: 0.6,
        minYellow: 0,
        maxYellow: 0.3999999,
        extraYellow: { min: 0.6000001, max: 0.8 },
        innerYellowCut: 0.2,
    },
    // Calidad de la deuda (PC/PT): green <= 0.3, yellow (0.3, 0.5], red > 0.5.
    calidadDeuda: {
        minGreen: 0,
        maxGreen: 0.3,
        minYellow: 0.3000001,
        maxYellow: 0.5,
    },
    // Márgenes (en %).
    margenBrutoPct: {
        minGreen: 50,
        maxGreen: Infinity,
        minYellow: 35,
        maxYellow: 49.9999999,
    },
    ebitdaMarginPct: {
        minGreen: 15,
        maxGreen: Infinity,
        minYellow: 5,
        maxYellow: 14.9999999,
    },
    netMarginPct: {
        minGreen: 8,
        maxGreen: Infinity,
        minYellow: 2,
        maxYellow: 7.9999999,
    },
    // Crecimiento (en %): green > 0, red <= 0.
    salesGrowth: {
        minGreen: 0.0000001,
        maxGreen: Infinity,
    },
};
const band = (color, range, meaning) => ({
    color,
    range,
    meaning,
});
export function semaforoBands(key) {
    const R = SEMAFORO_RULES;
    switch (key) {
        case 'liquidezCorriente':
            return [
                band('green', { kind: 'between', from: R.liquidezCorriente.minGreen, to: R.liquidezCorriente.maxGreen }, 'optimal'),
                band('yellow', {
                    kind: 'between',
                    from: R.liquidezCorriente.minYellow,
                    to: R.liquidezCorriente.minGreen,
                }, 'tight'),
                band('yellow', { kind: 'above', value: R.liquidezCorriente.maxGreen }, 'idle'),
                band('red', { kind: 'below', value: R.liquidezCorriente.minYellow }, 'risk'),
            ];
        case 'pruebaAcida':
            return [
                band('green', { kind: 'between', from: R.pruebaAcida.minGreen, to: R.pruebaAcida.maxGreen }, 'optimal'),
                band('yellow', { kind: 'between', from: R.pruebaAcida.minYellow, to: R.pruebaAcida.minGreen }, 'tight'),
                band('yellow', { kind: 'above', value: R.pruebaAcida.maxGreen }, 'idle'),
                band('red', { kind: 'below', value: R.pruebaAcida.minYellow }, 'risk'),
            ];
        case 'fondoManiobra':
            return [
                band('green', { kind: 'positive' }, 'positive'),
                band('red', { kind: 'nonPositive' }, 'negative'),
            ];
        case 'endeudamiento':
            return [
                band('green', { kind: 'between', from: R.endeudamiento.minGreen, to: R.endeudamiento.maxGreen }, 'balanced'),
                band('yellow', { kind: 'between', from: R.endeudamiento.innerYellowCut, to: R.endeudamiento.minGreen }, 'conservative'),
                band('yellow', { kind: 'between', from: R.endeudamiento.minYellow, to: R.endeudamiento.innerYellowCut }, 'underleveraged'),
                band('yellow', { kind: 'between', from: R.endeudamiento.maxGreen, to: R.endeudamiento.extraYellow.max }, 'high'),
                band('red', { kind: 'above', value: R.endeudamiento.extraYellow.max }, 'excess'),
                band('red', { kind: 'below', value: R.endeudamiento.minYellow }, 'negativeEquity'),
            ];
        case 'calidadDeuda':
            // PC / PT solo tiene sentido en [0, 1]: un cociente negativo (pasivo con saldo deudor) no es
            // «buena calidad» aunque el texto de la tarjeta lo diga, y la regla lo pinta rojo. Es un
            // caso de datos raros, no una banda que merezca enseñarse.
            return [
                band('green', { kind: 'atMost', value: R.calidadDeuda.maxGreen }, 'good'),
                band('yellow', { kind: 'between', from: R.calidadDeuda.maxGreen, to: R.calidadDeuda.maxYellow }, 'balanced'),
                band('red', { kind: 'above', value: R.calidadDeuda.maxYellow }, 'shortTermPressure'),
            ];
        case 'margenBrutoPct':
            return [
                band('green', { kind: 'atLeast', value: R.margenBrutoPct.minGreen }, 'wide'),
                band('yellow', { kind: 'between', from: R.margenBrutoPct.minYellow, to: R.margenBrutoPct.minGreen }, 'acceptable'),
                band('red', { kind: 'below', value: R.margenBrutoPct.minYellow }, 'narrow'),
            ];
        case 'ebitdaMarginPct':
            return [
                band('green', { kind: 'atLeast', value: R.ebitdaMarginPct.minGreen }, 'solid'),
                band('yellow', { kind: 'between', from: R.ebitdaMarginPct.minYellow, to: R.ebitdaMarginPct.minGreen }, 'moderate'),
                band('red', { kind: 'between', from: 0, to: R.ebitdaMarginPct.minYellow }, 'thin'),
                band('red', { kind: 'below', value: 0 }, 'negative'),
            ];
        case 'netMarginPct':
            return [
                band('green', { kind: 'atLeast', value: R.netMarginPct.minGreen }, 'good'),
                band('yellow', { kind: 'between', from: R.netMarginPct.minYellow, to: R.netMarginPct.minGreen }, 'tight'),
                band('red', { kind: 'between', from: 0, to: R.netMarginPct.minYellow }, 'thin'),
                band('red', { kind: 'below', value: 0 }, 'loss'),
            ];
        case 'salesGrowthMoM':
        case 'salesGrowthYoY':
            return [
                band('green', { kind: 'positive' }, 'growing'),
                band('red', { kind: 'nonPositive' }, 'shrinking'),
            ];
    }
}
export function bandContains(range, v) {
    switch (range.kind) {
        case 'between':
            return v >= range.from && v <= range.to;
        case 'atLeast':
            return v >= range.value;
        case 'atMost':
            return v <= range.value;
        case 'above':
            return v > range.value;
        case 'below':
            return v < range.value;
        case 'positive':
            return v > 0;
        case 'nonPositive':
            return v <= 0;
    }
}
/** La banda que le toca a un valor (la mejor si cae en un borde), o null si no hay ninguna. */
export function semaforoBandFor(key, v) {
    return semaforoBands(key).find((b) => bandContains(b.range, v)) ?? null;
}
//# sourceMappingURL=accounting-semaforo.js.map